import React, { useState, useEffect } from 'react';
import { Wallet, Coins, DollarSign, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import WalletService, { WalletBalance as WalletBalanceData } from '../services/WalletService';

interface WalletBalanceProps {
  walletAddress: string;
  showTitle?: boolean;
  className?: string;
}

const WalletBalance: React.FC<WalletBalanceProps> = ({ 
  walletAddress, 
  showTitle = true,
  className = ""
}) => {
  const [balance, setBalance] = useState<WalletBalanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (walletAddress) {
      fetchBalance();
    }
  }, [walletAddress]);

  const fetchBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const balanceData = await WalletService.getWalletBalance(walletAddress);
      setBalance(balanceData);
    } catch (err) {
      setError('Failed to fetch wallet balance');
      console.error('Error fetching wallet balance:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!walletAddress) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <Wallet className="h-5 w-5" />
          <span className="text-sm">No wallet address provided</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Wallet Balance</h3>
          </div>
          <button
            onClick={fetchBalance}
            disabled={loading}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Wallet Address */}
      <div className="mb-4">
        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Wallet Address</label>
        <p className="text-sm font-mono text-gray-700 break-all">
          {walletAddress}
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-green-600" />
          <span className="ml-2 text-gray-600">Loading balance...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-center justify-center py-8 text-red-600">
          <AlertCircle className="h-6 w-6 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Balance Display */}
      {balance && !loading && !error && (
        <div className="grid grid-cols-2 gap-4">
          {/* SOL Balance */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Coins className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">SOL Balance</span>
            </div>
            <p className="text-lg font-bold text-green-900">
              {balance.sol_balance.toFixed(6)}
            </p>
          </div>

          {/* PYUSD Balance */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-gray-700">PYUSD Balance</span>
            </div>
            <p className="text-lg font-bold text-green-900">
              {balance.pyusd_balance.toFixed(6)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletBalance;
